import { useCallback, useEffect, useRef, useState } from "react"
import { SortTypes } from "./types"

type SortHandler = (sortBy: string, order: SortTypes) => void

export const useSorting = (
  sortBy?: string,
  order?: SortTypes,
  onSort?: SortHandler
) => {
  const [currentSortBy, setCurrentSortBy] = useState<string>(sortBy ?? null)
  const [currentOrder, setCurrentOrder] = useState<SortTypes>(order ?? null)

  useEffect(() => {
    setCurrentSortBy(sortBy ?? null)
    setCurrentOrder(order ?? null)
  }, [sortBy, order])

  const setCurrentSort: SortHandler = useCallback(
    (sortBy, order) => {
      setCurrentSortBy(sortBy)
      setCurrentOrder(order)
      onSort?.(sortBy, order)
    },
    [onSort]
  )

  return {
    order: currentOrder,
    sortBy: currentSortBy,
    onSort: setCurrentSort
  }
}

export const useScrollToBottom = (onScrollToBottom?: () => void) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (container) {
      const scrollHandler = (event: Event) => {
        const target = event.currentTarget as HTMLDivElement
        const { scrollTop, scrollHeight, clientHeight } = target

        if (scrollTop + clientHeight >= scrollHeight - 1) {
          onScrollToBottom?.()
        }
      }

      container.addEventListener("scroll", scrollHandler)

      return () => {
        container.removeEventListener("scroll", scrollHandler)
      }
    }
    return null
  }, [onScrollToBottom])

  return containerRef
}
